import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

// Icon names enum
export const IconNames = {
  BED_SINGLE: 'bed-single',
  CAMERA: 'camera',
  MOBILE_KEY: 'mobile-key',
  LOCATION_PIN: 'location-pin',
  GIFT: 'gift',
  COPY: 'copy',
  FEEDBACK: 'feedback',
  MAP: 'map',
  OPENING_HOURS: 'opening-hours',
  MY_STAY: 'my-stay',
  EDIT: 'edit',
  PRICE_TAG: 'price-tag',
  RATING: 'rating',
  USER: 'user',
  CONTACT_PHONE: 'contact-phone',
  REFRESH: 'refresh',
  CLOSE: 'close',
  RECEIPT: 'receipt',
  CREDIT_CARD: 'credit-card',
  CHECK_IN_OUT: 'check-in-out',
  EMAIL_CONTACT: 'email-contact',
  CHEVRON_UP: 'chevron-up',
  ADD: 'add',
  BREAKFAST: 'breakfast',
  DEPARTURE_DATE: 'departure-date',
};

const StrawberryIcon = ({ name, size = 24, color = 'currentColor', className = '' }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // If color is currentColor, use the theme's text color
  const effectiveColor = color === 'currentColor' 
    ? isDarkMode ? '#FFFFFF' : theme.palette.text.primary
    : color;

  // Fix the path to work in both development and production
  const iconPath = `/assets/icons/${name}.svg`;

  return (
    <img
      src={iconPath}
      alt={name}
      style={{
        width: size,
        height: size,
        filter: effectiveColor === '#FFFFFF' 
          ? 'brightness(0) saturate(100%) invert(1)' 
          : `brightness(0) saturate(100%) ${getColorFilter(effectiveColor)}`,
        opacity: isDarkMode ? 1 : 0.87, // Slightly reduce opacity in light mode for better contrast
        transition: 'filter 0.2s ease, opacity 0.2s ease', // Smooth transition for color changes
      }}
      className={className}
    />
  );
};

// Helper function to generate color filter
const getColorFilter = (color) => {
  const rgb = hexToRgb(color);
  if (!rgb) return '';

  // Convert RGB to HSL
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  return `
    sepia(${Math.round(hsl.s * 100)}%) 
    saturate(${Math.round((hsl.s + 0.2) * 400)}%) 
    hue-rotate(${Math.round(hsl.h)}deg) 
    brightness(${Math.round(hsl.l * 100)}%)
  `.trim();
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to convert RGB to HSL
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return { h: h * 360, s, l };
};

StrawberryIcon.propTypes = {
  name: PropTypes.oneOf(Object.values(IconNames)).isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
};

export default StrawberryIcon; 